import React, { useState } from 'react';
import classNames from 'classnames';
import { ToggleGroup, ToggleGroupItem } from '$/components/ToggleGroup';
import styles from './index.module.scss';

export interface RatioOption {
  value: string | number | boolean;
  label: React.ReactNode;
  icon?: string;
  disabled?: boolean;
}

export const RATIO_OPTIONS: RatioOption[] = [
  { value: 'auto', label: '自适应', icon: 'ratioAuto' },
  { value: '1:1', label: '1:1', icon: 'ratio1x1' },
  { value: '9:16', label: '9:16', icon: 'ratio9x16' },
  { value: '16:9', label: '16:9', icon: 'ratio16x9' },
  { value: '3:4', label: '3:4', icon: 'ratio3x4' },
  { value: '4:3', label: '4:3', icon: 'ratio4x3' },
  { value: '3:2', label: '3:2', icon: 'ratio3x2' },
  { value: '2:3', label: '2:3', icon: 'ratio2x3' },
  { value: '4:5', label: '4:5', icon: 'ratio4x5' },
  { value: '5:4', label: '5:4', icon: 'ratio5x4' },
  { value: '21:9', label: '21:9', icon: 'ratio21x9' },
];

const OPTION_ROW_GAP = 6;

function chunkOptions<T>(options: T[], maxItemsPerRow: number): T[][] {
  const rows: T[][] = [];
  const safeSize = Math.max(1, maxItemsPerRow);

  for (let i = 0; i < options.length; i += safeSize) {
    rows.push(options.slice(i, i + safeSize));
  }

  return rows;
}

function getOptionRowStyle(maxItemsPerRow: number): React.CSSProperties {
  const safeSize = Math.max(1, maxItemsPerRow);
  const occupiedGap = (safeSize - 1) * OPTION_ROW_GAP;

  return {
    '--team-flow-ratio-item-width': `calc(${100 / safeSize}% - ${occupiedGap / safeSize}px)`,
  } as React.CSSProperties;
}

function getRatioIconStyle(value: string, maxSize = 18): React.CSSProperties | null {
  const [widthValue, heightValue] = value.split(':').map(Number);

  if (!widthValue || !heightValue) return null;

  const ratio = widthValue / heightValue;
  const width = ratio >= 1 ? maxSize : maxSize * ratio;
  const height = ratio >= 1 ? maxSize / ratio : maxSize;

  return {
    width: `${width}px`,
    height: `${height}px`,
  };
}

export function RatioIcon({ value, size = 18 }: { value: string; size?: number; }) {
  const iconStyle = getRatioIconStyle(value, size);

  if (!iconStyle) return null;

  return (
    <span
      className={styles.iconViewport}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span className={styles.iconShape} style={iconStyle} />
    </span>
  );
}

export interface RatioProps {
  value?: string | number | boolean;
  defaultValue?: string | number | boolean;
  options?: RatioOption[];
  disabled?: boolean;
  maxItemsPerRow?: number;
  className?: string;
  rowClassName?: string;
  itemClassName?: string;
  labelClassName?: string;
  onChange?: (value: string | number | boolean, option?: RatioOption) => void;
}

export default function Ratio({
  value,
  defaultValue,
  options = RATIO_OPTIONS,
  disabled = false,
  maxItemsPerRow = 4,
  className,
  rowClassName,
  itemClassName,
  labelClassName,
  onChange,
}: RatioProps) {
  const resolvedOptions = options.length ? options : RATIO_OPTIONS;
  const [innerValue, setInnerValue] = useState(defaultValue ?? resolvedOptions[0]?.value);
  const currentValue = value ?? innerValue;
  const rowStyle = getOptionRowStyle(maxItemsPerRow);

  return (
    <ToggleGroup
      type="single"
      value={currentValue !== undefined ? String(currentValue) : undefined}
      disabled={disabled}
      className={classNames(styles.group, className)}
      onValueChange={(nextValue: string) => {
        if (!nextValue) return;
        setInnerValue(nextValue);
        const sourceOption = resolvedOptions.find((option) => String(option.value) === nextValue);
        onChange?.(
          sourceOption?.value ?? nextValue,
          sourceOption,
        );
      }}
    >
      {chunkOptions(resolvedOptions, maxItemsPerRow).map((row) => (
        <div
          key={row.map((option) => String(option.value)).join('-')}
          className={classNames(styles.row, rowClassName)}
          style={rowStyle}
        >
          {row.map((option) => {
            const optionValue = String(option.value);

            return (
              <ToggleGroupItem
                key={optionValue}
                value={optionValue}
                disabled={option.disabled}
                className={classNames(styles.item, itemClassName)}
              >
                <RatioIcon value={optionValue} />
                <span className={classNames(styles.label, labelClassName)}>
                  {option.label}
                </span>
              </ToggleGroupItem>
            );
          })}
        </div>
      ))}
    </ToggleGroup>
  );
}
